import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PriorityHigh as PriorityHighIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { Task, Contact, Initiative } from '../../types/database';
import { TaskModal } from './TaskModal';

export const TaskList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const db = useDatabase();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openTaskModal, setOpenTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [viewBy, setViewBy] = useState<'initiative' | 'priority' | 'none'>('priority');
  const [filterExpanded, setFilterExpanded] = useState(false);
  
  // Get status filter from URL, default to 'pending' if no filter is set
  const statusFilter = new URLSearchParams(location.search).get('status') || 'pending';
  
  // Get assignee filter from URL, default to 'all' if no filter is set
  const assigneeFilter = new URLSearchParams(location.search).get('assignee') || 'all';
  
  // Get time_tag filter from URL, default to 'all' if no filter is set
  const timeTagFilter = new URLSearchParams(location.search).get('time_tag') || 'all';

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load tasks, contacts, and initiatives
      const [tasksResponse, contactsResponse, initiativesResponse] = await Promise.all([
        db.getTasks(),
        db.getContacts(),
        db.getInitiatives(),
      ]);
      
      if (tasksResponse.error) throw tasksResponse.error;
      if (contactsResponse.error) throw contactsResponse.error;
      if (initiativesResponse.error) throw initiativesResponse.error;
      
      setTasks(tasksResponse.data || []);
      setContacts(contactsResponse.data || []);
      setInitiatives(initiativesResponse.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleStatusChange = (newStatus: string) => {
    const params = new URLSearchParams(location.search);
    if (newStatus === 'pending') {
      params.delete('status');
    } else {
      params.set('status', newStatus);
    }
    
    const queryString = params.toString();
    navigate(`/tasks${queryString ? `?${queryString}` : ''}`);
  };

  const handleAssigneeChange = (newAssignee: string) => {
    const params = new URLSearchParams(location.search);
    if (newAssignee === 'all') {
      params.delete('assignee');
    } else {
      params.set('assignee', newAssignee);
    }
    
    const queryString = params.toString();
    navigate(`/tasks${queryString ? `?${queryString}` : ''}`);
  };

  const handleTimeTagChange = (newTimeTag: string) => {
    const params = new URLSearchParams(location.search);
    if (newTimeTag === 'all') {
      params.delete('time_tag');
    } else {
      params.set('time_tag', newTimeTag);
    }
    
    const queryString = params.toString();
    navigate(`/tasks${queryString ? `?${queryString}` : ''}`);
  };

  const handleViewByChange = (newViewBy: 'initiative' | 'priority' | 'none') => {
    setViewBy(newViewBy);
  };

  // Helper function to get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (statusFilter !== 'pending') count++;
    if (assigneeFilter !== 'all') count++;
    if (timeTagFilter !== 'all') count++;
    return count;
  };

  // Helper function to clear all filters
  const clearAllFilters = () => {
    navigate('/tasks');
  };

  // Helper function to get filter display name
  const getFilterDisplayName = (type: string, value: string) => {
    const displayNames: { [key: string]: { [key: string]: string } } = {
      status: {
        all: 'All',
        pending: 'Pending',
        in_progress: 'In Progress',
        completed: 'Completed',
        cancelled: 'Cancelled',
      },
      assignee: {
        Me: 'My Tasks',
        MyCatalyze: 'MyCatalyze Tasks',
      },
      time_tag: {
        today: 'Today',
        tomorrow: 'Tomorrow',
        this_week: 'This Week',
        next_week: 'Next Week',
        next_month: 'Next Month',
        later: 'Later',
        no_time: 'No Time Set',
      },
    };
    return displayNames[type]?.[value] || value;
  };

  const handleAddTask = async (formData: Omit<Task, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await db.createTask(formData);
      if (error) throw error;
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task');
    }
  };

  const handleEditTask = async (formData: Omit<Task, 'id' | 'created_at' | 'updated_at'>) => {
    if (!selectedTask) return;
    
    try {
      const { error } = await db.updateTask(selectedTask.id, formData);
      if (error) throw error;
      await loadTasks();
      setSelectedTask(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const { error } = await db.deleteTask(taskId);
      if (error) throw error;
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  const handleTaskStatusChange = async (taskId: string, newStatus: Task['status']) => {
    try {
      const { error } = await db.updateTask(taskId, { status: newStatus });
      if (error) throw error;
      await loadTasks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task status');
    }
  };



  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon />;
      case 'in_progress': return <ScheduleIcon />;
      case 'pending': return <ScheduleIcon />;
      case 'cancelled': return <ScheduleIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const getEntityName = (task: Task) => {
    if (!task.entity_type || !task.entity_id) return null;
    
    if (task.entity_type === 'contact') {
      const contact = contacts.find(c => c.id === task.entity_id);
      return contact?.name || 'Unknown Contact';
    }
    
    if (task.entity_type === 'initiative') {
      const initiative = initiatives.find(i => i.id === task.entity_id);
      return initiative?.name || 'Unknown Initiative';
    }
    
    return null;
  };

  const filteredTasks = tasks.filter(task => {
    // Apply status filter
    const statusMatch = statusFilter === 'all' || task.status === statusFilter;
    
    // Apply assignee filter
    const assigneeMatch = assigneeFilter === 'all' || task.assignee === assigneeFilter;
    
    // Apply time_tag filter
    let timeTagMatch = true;
    if (timeTagFilter === 'all') {
      timeTagMatch = true;
    } else if (timeTagFilter === 'no_time') {
      timeTagMatch = !task.time_tag || (task.time_tag as string) === '';
    } else {
      timeTagMatch = task.time_tag === timeTagFilter;
    }
    
    return statusMatch && assigneeMatch && timeTagMatch;
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    // First, put completed tasks at the bottom
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    
    // For non-completed tasks, sort by due date first (tasks without due dates go to the end)
    const dateA = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    const dateB = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    if (dateA !== dateB) return dateA - dateB;
    
    // Then by priority
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });

  // Group tasks by initiative if grouping is enabled
  const groupedTasks = viewBy === 'initiative' ? (() => {
    const groups: { [key: string]: Task[] } = {};
    
    // Group by initiative
    sortedTasks.forEach(task => {
      if (task.entity_type === 'initiative' && task.entity_id) {
        const initiative = initiatives.find(i => i.id === task.entity_id);
        const groupKey = initiative ? initiative.name : 'Unknown Initiative';
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(task);
      } else {
        // Tasks not linked to initiatives go to "Unlinked" group
        if (!groups['Unlinked']) {
          groups['Unlinked'] = [];
        }
        groups['Unlinked'].push(task);
      }
    });
    
    // Sort groups to put "Unlinked" at the top, then alphabetically by initiative name
    const sortedGroups: { [key: string]: Task[] } = {};
    const groupNames = Object.keys(groups).sort((a, b) => {
      if (a === 'Unlinked') return -1;
      if (b === 'Unlinked') return 1;
      return a.localeCompare(b);
    });
    
    groupNames.forEach(name => {
      sortedGroups[name] = groups[name];
    });
    
    return sortedGroups;
  })() : null;

  // Group tasks by priority if priority grouping is enabled
  const groupedByPriority = viewBy === 'priority' ? (() => {
    const groups: { [key: string]: Task[] } = {};
    
    // Group by priority
    sortedTasks.forEach(task => {
      const priorityKey = task.priority.charAt(0).toUpperCase() + task.priority.slice(1);
      if (!groups[priorityKey]) {
        groups[priorityKey] = [];
      }
      groups[priorityKey].push(task);
    });
    
    // Sort groups by priority order: High, Medium, Low
    const priorityOrder = { High: 1, Medium: 2, Low: 3 };
    const sortedGroups: { [key: string]: Task[] } = {};
    const groupNames = Object.keys(groups).sort((a, b) => {
      return priorityOrder[a as keyof typeof priorityOrder] - priorityOrder[b as keyof typeof priorityOrder];
    });
    
    groupNames.forEach(name => {
      sortedGroups[name] = groups[name];
    });
    
    return sortedGroups;
  })() : null;

  // Helper function to get the appropriate grouped data
  const getGroupedData = () => {
    if (viewBy === 'initiative' && groupedTasks) return groupedTasks;
    if (viewBy === 'priority' && groupedByPriority) return groupedByPriority;
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Tasks">
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 }, backgroundColor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Mobile-optimized Header */}
        <Box sx={{ mb: 2 }}>
          {/* Top row: New Task button and Filter toggle */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            gap: 1
          }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenTaskModal(true)}
              sx={{ 
                borderRadius: 2, 
                height: 44,
                minWidth: { xs: 'auto', sm: 120 },
                px: { xs: 2, sm: 3 },
                flexShrink: 0,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>New Task</Box>
              <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
            </Button>

            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              endIcon={<ExpandMoreIcon sx={{ 
                transform: filterExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} />}
              onClick={() => setFilterExpanded(true)}
              sx={{ 
                borderRadius: 2,
                height: 44,
                minWidth: { xs: 'auto', sm: 120 },
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.875rem', sm: '0.9375rem' }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Filters</Box>
              <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>Filter</Box>
            </Button>
          </Box>
          
          {/* Active filter chips - mobile optimized */}
          {getActiveFilterCount() > 0 && (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1,
              mb: 1
            }}>
              {statusFilter !== 'pending' && (
                <Chip
                  label={`Status: ${getFilterDisplayName('status', statusFilter)}`}
                  size="small"
                  onDelete={() => handleStatusChange('pending')}
                  color="success"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 28,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              )}
              {assigneeFilter !== 'all' && (
                <Chip
                  label={`Assignee: ${getFilterDisplayName('assignee', assigneeFilter)}`}
                  size="small"
                  onDelete={() => handleAssigneeChange('all')}
                  color="info"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 28,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              )}
              {timeTagFilter !== 'all' && (
                <Chip
                  label={`Time: ${getFilterDisplayName('time_tag', timeTagFilter)}`}
                  size="small"
                  onDelete={() => handleTimeTagChange('all')}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 28,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              )}
              <Chip
                label="Clear All"
                size="small"
                onClick={clearAllFilters}
                onDelete={clearAllFilters}
                color="default"
                variant="outlined"
                icon={<ClearIcon />}
                sx={{ 
                  fontSize: '0.75rem',
                  height: 28,
                  '& .MuiChip-label': {
                    px: 1
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Mobile-optimized Filter Panel */}
        <Collapse in={filterExpanded}>
          <Paper 
            sx={{ 
              mb: 2,
              boxShadow: 1,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Filter Header with Close Button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: 'action.hover',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                Filters
              </Typography>
              <IconButton
                size="small"
                onClick={() => setFilterExpanded(false)}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    backgroundColor: 'action.selected',
                    color: 'text.primary'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            {/* Filter Content */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={{ xs: 2, sm: 3 }}>
                {/* View Options */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                    Group By
                  </Typography>
                  <ToggleButtonGroup
                    value={viewBy}
                    exclusive
                    onChange={(_, value) => value && handleViewByChange(value)}
                    size="small"
                    sx={{ 
                      flexWrap: 'wrap',
                      '& .MuiToggleButton-root': {
                        fontSize: '0.75rem',
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        minHeight: 32
                      }
                    }}
                  >
                    <ToggleButton value="priority">Priority</ToggleButton>
                    <ToggleButton value="initiative">Initiative</ToggleButton>
                    <ToggleButton value="none">None</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Status Filter */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                    Status
                  </Typography>
                  <ToggleButtonGroup
                    value={statusFilter}
                    exclusive
                    onChange={(_, value) => value && handleStatusChange(value)}
                    size="small"
                    sx={{ 
                      flexWrap: 'wrap',
                      '& .MuiToggleButton-root': {
                        fontSize: '0.75rem',
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        minHeight: 32
                      }
                    }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="pending">Pending</ToggleButton>
                    <ToggleButton value="in_progress">In Progress</ToggleButton>
                    <ToggleButton value="completed">Completed</ToggleButton>
                    <ToggleButton value="cancelled">Cancelled</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Assignee Filter */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                    Assignee
                  </Typography>
                  <ToggleButtonGroup
                    value={assigneeFilter}
                    exclusive
                    onChange={(_, value) => value && handleAssigneeChange(value)}
                    size="small"
                    sx={{ 
                      flexWrap: 'wrap',
                      '& .MuiToggleButton-root': {
                        fontSize: '0.75rem',
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        minHeight: 32
                      }
                    }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="Me">My Tasks</ToggleButton>
                    <ToggleButton value="MyCatalyze">MyCatalyze Tasks</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Time Tag Filter */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                    Time Frame
                  </Typography>
                  <ToggleButtonGroup
                    value={timeTagFilter}
                    exclusive
                    onChange={(_, value) => value && handleTimeTagChange(value)}
                    size="small"
                    sx={{ 
                      flexWrap: 'wrap',
                      '& .MuiToggleButton-root': {
                        fontSize: '0.75rem',
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        minHeight: 32
                      }
                    }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="tomorrow">Tomorrow</ToggleButton>
                    <ToggleButton value="this_week">This Week</ToggleButton>
                    <ToggleButton value="next_week">Next Week</ToggleButton>
                    <ToggleButton value="next_month">Next Month</ToggleButton>
                    <ToggleButton value="later">Later</ToggleButton>
                    <ToggleButton value="no_time">No Time Set</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Collapse>

        <Paper 
          sx={{ 
            width: '100%', 
            mb: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {sortedTasks.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {statusFilter === 'all' && assigneeFilter === 'all' && timeTagFilter === 'all'
                  ? 'No tasks found' 
                  : `No tasks found matching current filters`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {statusFilter === 'all' && assigneeFilter === 'all' && timeTagFilter === 'all'
                  ? 'No tasks available' 
                  : 'Try changing the status, assignee, or time tag filters'}
              </Typography>
            </Box>
          ) : getGroupedData() ? (
            // Render grouped tasks (mobile-optimized)
            <Box>
              {Object.entries(getGroupedData()!).map(([groupName, groupTasks]) => (
                <Box key={groupName} sx={{ mb: 3 }}>
                  <Box sx={{ 
                    px: { xs: 2, sm: 3 }, 
                    py: 2, 
                    backgroundColor: 'action.hover',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {groupName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {groupTasks.length} task{groupTasks.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 0 }}>
                    {groupTasks.map((task, idx) => (
                      <Box key={task.id}>
                        {/* Mobile-optimized Task Card */}
                        <Box sx={{ 
                          p: { xs: 2, sm: 3 },
                          borderBottom: idx < groupTasks.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}>
                          {/* Task Header */}
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            mb: 1
                          }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 500, 
                                color: 'text.primary',
                                textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                                opacity: task.status === 'completed' ? 0.6 : 1,
                                flex: 1,
                                pr: 1,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                              }}
                            >
                              {task.title}
                            </Typography>
                            
                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {task.status !== 'completed' && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleTaskStatusChange(task.id, 'completed')}
                                  sx={{ 
                                    color: 'success.main',
                                    p: 0.5,
                                    '&:hover': { backgroundColor: 'success.light', color: 'white' }
                                  }}
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => setSelectedTask(task)}
                                sx={{ 
                                  p: 0.5,
                                  '&:hover': { backgroundColor: 'action.hover' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTask(task.id)}
                                sx={{ 
                                  color: 'error.main',
                                  p: 0.5,
                                  '&:hover': { backgroundColor: 'error.light', color: 'white' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Task Description */}
                          {task.description && (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                mb: 1.5,
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                lineHeight: 1.4
                              }}
                            >
                              {task.description}
                            </Typography>
                          )}

                          {/* Task Tags - Mobile Optimized */}
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 0.5,
                            alignItems: 'center'
                          }}>
                            {getEntityName(task) && (
                              <Chip
                                label={getEntityName(task)}
                                size="small"
                                variant="outlined"
                                icon={task.entity_type === 'contact' ? <PersonIcon /> : <AssignmentIcon />}
                                onClick={() => navigate(`/${task.entity_type}s/${task.entity_id}`)}
                                sx={{
                                  borderColor: task.entity_type === 'contact' ? 'primary.main' : 'success.main',
                                  color: task.entity_type === 'contact' ? 'primary.main' : 'success.main',
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  fontSize: '0.7rem',
                                  height: 24,
                                  '&:hover': {
                                    backgroundColor: task.entity_type === 'contact' ? 'primary.light' : 'success.light',
                                    color: 'white',
                                  },
                                  '& .MuiChip-label': {
                                    px: 1,
                                    color: 'inherit',
                                  },
                                  '& .MuiChip-icon': {
                                    color: 'inherit',
                                    fontSize: '0.875rem'
                                  },
                                }}
                              />
                            )}
                            
                            <Chip
                              label={task.status.replace('_', ' ')}
                              size="small"
                              variant="outlined"
                              icon={getStatusIcon(task.status)}
                              sx={{
                                borderColor: 'text.secondary',
                                color: 'text.secondary',
                                backgroundColor: 'transparent',
                                fontSize: '0.7rem',
                                height: 24,
                                '& .MuiChip-label': {
                                  px: 1,
                                  color: 'text.secondary',
                                },
                                '& .MuiChip-icon': {
                                  color: 'text.secondary',
                                  fontSize: '0.875rem'
                                },
                              }}
                            />
                            
                            <Chip
                              label={task.priority}
                              size="small"
                              variant="outlined"
                              icon={<PriorityHighIcon />}
                              sx={{
                                borderColor: 'text.secondary',
                                color: 'text.secondary',
                                backgroundColor: 'transparent',
                                fontSize: '0.7rem',
                                height: 24,
                                '& .MuiChip-label': {
                                  px: 1,
                                  color: 'text.secondary',
                                },
                                '& .MuiChip-icon': {
                                  color: 'text.secondary',
                                  fontSize: '0.875rem'
                                },
                              }}
                            />
                            
                            <Chip
                              label={task.assignee}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: 'text.secondary',
                                color: 'text.secondary',
                                backgroundColor: 'transparent',
                                fontSize: '0.7rem',
                                height: 24,
                                '& .MuiChip-label': {
                                  px: 1,
                                  color: 'text.secondary',
                                },
                              }}
                            />
                            
                            {task.time_tag && (
                              <Chip
                                label={task.time_tag.replace('_', ' ')}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  backgroundColor: 'transparent',
                                  fontSize: '0.7rem',
                                  height: 24,
                                  '& .MuiChip-label': {
                                    px: 1,
                                    color: 'primary.main',
                                  },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            // Render ungrouped tasks (mobile-optimized view)
            <Box sx={{ p: 0 }}>
              {sortedTasks.map((task, idx) => (
                <Box key={task.id}>
                  {/* Mobile-optimized Task Card */}
                  <Box sx={{ 
                    p: { xs: 2, sm: 3 },
                    borderBottom: idx < sortedTasks.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}>
                    {/* Task Header */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      mb: 1
                    }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 600, 
                          color: 'text.primary',
                          textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                          opacity: task.status === 'completed' ? 0.6 : 1,
                          flex: 1,
                          pr: 1,
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {task.title}
                      </Typography>
                      
                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {task.status !== 'completed' && (
                          <IconButton
                            size="small"
                            onClick={() => handleTaskStatusChange(task.id, 'completed')}
                            sx={{ 
                              color: 'success.main',
                              p: 0.5,
                              '&:hover': { backgroundColor: 'success.light', color: 'white' }
                            }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => setSelectedTask(task)}
                          sx={{ 
                            p: 0.5,
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTask(task.id)}
                          sx={{ 
                            color: 'error.main',
                            p: 0.5,
                            '&:hover': { backgroundColor: 'error.light', color: 'white' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Task Description */}
                    {task.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 1.5,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          lineHeight: 1.4
                        }}
                      >
                        {task.description}
                      </Typography>
                    )}

                    {/* Task Tags - Mobile Optimized */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 0.5,
                      alignItems: 'center'
                    }}>
                      {getEntityName(task) && (
                        <Chip
                          label={getEntityName(task)}
                          size="small"
                          variant="outlined"
                          icon={task.entity_type === 'contact' ? <PersonIcon /> : <AssignmentIcon />}
                          onClick={() => navigate(`/${task.entity_type}s/${task.entity_id}`)}
                          sx={{
                            borderColor: task.entity_type === 'contact' ? 'primary.main' : 'success.main',
                            color: task.entity_type === 'contact' ? 'primary.main' : 'success.main',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            height: 24,
                            '&:hover': {
                              backgroundColor: task.entity_type === 'contact' ? 'primary.light' : 'success.light',
                              color: 'white',
                            },
                            '& .MuiChip-label': {
                              px: 1,
                              color: 'inherit',
                            },
                            '& .MuiChip-icon': {
                              color: 'inherit',
                              fontSize: '0.875rem'
                            },
                          }}
                        />
                      )}
                      
                      <Chip
                        label={task.status.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                        icon={getStatusIcon(task.status)}
                        sx={{
                          borderColor: 'text.secondary',
                          color: 'text.secondary',
                          backgroundColor: 'transparent',
                          fontSize: '0.7rem',
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1,
                            color: 'text.secondary',
                          },
                          '& .MuiChip-icon': {
                            color: 'text.secondary',
                            fontSize: '0.875rem'
                          },
                        }}
                      />
                      
                      <Chip
                        label={task.priority}
                        size="small"
                        variant="outlined"
                        icon={<PriorityHighIcon />}
                        sx={{
                          borderColor: 'text.secondary',
                          color: 'text.secondary',
                          backgroundColor: 'transparent',
                          fontSize: '0.7rem',
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1,
                            color: 'text.secondary',
                          },
                          '& .MuiChip-icon': {
                            color: 'text.secondary',
                            fontSize: '0.875rem'
                          },
                        }}
                      />
                      
                      <Chip
                        label={task.assignee}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: 'text.secondary',
                          color: 'text.secondary',
                          backgroundColor: 'transparent',
                          fontSize: '0.7rem',
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1,
                            color: 'text.secondary',
                          },
                        }}
                      />
                      
                      {task.time_tag && (
                        <Chip
                          label={task.time_tag.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            backgroundColor: 'transparent',
                            fontSize: '0.7rem',
                            height: 24,
                            '& .MuiChip-label': {
                              px: 1,
                              color: 'primary.main',
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <TaskModal
          open={openTaskModal}
          onClose={() => setOpenTaskModal(false)}
          onSubmit={handleAddTask}
        />

        <TaskModal
          open={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          onSubmit={handleEditTask}
          task={selectedTask}
        />
      </Box>
    </DashboardLayout>
  );
}; 